import { useState } from 'react';
import { Text, Spinner, Button, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { useT } from '../i18n';
import { fetchEvidenceFolders, fetchEvidencePhotos, fetchEvidencePhotoUrl, type EvidenceFolder, type EvidencePhoto } from '../api/evidence.api';

/**
 * Visor de evidencias: carpetas por lote (y buffers pendientes por usuario) con las
 * fotos de la Mini App como miniaturas. Abrir/descargar usan una URL firmada de 15
 * min que pide el servidor, así que el bucket sigue privado.
 */

const useStyles = makeStyles({
  root:    { display: 'flex', gap: tokens.spacingHorizontalL, padding: tokens.spacingVerticalL, flexWrap: 'wrap' },
  folders: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: '220px' },
  folder:  { justifyContent: 'flex-start' },
  photos:  { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalM, flex: 1, minWidth: '300px' },
  card:    { width: '180px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, padding: tokens.spacingVerticalXS },
  thumb:   { width: '100%', height: '140px', objectFit: 'cover', borderRadius: tokens.borderRadiusSmall, background: tokens.colorNeutralBackground3 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalXS },
});

function PhotoCard({ photo }: { photo: EvidencePhoto }) {
  const t = useT();
  const s = useStyles();
  const { data: url, isLoading } = useQuery({
    queryKey: ['evidence-photo-url', photo.key],
    queryFn: () => fetchEvidencePhotoUrl(photo.key, false),
    staleTime: 10 * 60 * 1000,
  });
  const openFull = async () => window.open(await fetchEvidencePhotoUrl(photo.key, false), '_blank', 'noopener');
  const download = async () => window.open(await fetchEvidencePhotoUrl(photo.key, true), '_blank', 'noopener');

  return (
    <div className={s.card}>
      {isLoading || !url ? <Spinner size="tiny" /> : <img className={s.thumb} src={url} alt={photo.key.split('/').pop()} loading="lazy" />}
      <Text size={200}>{new Date(photo.lastModified).toLocaleString('es-MX')}</Text>
      <Text size={100}>{(photo.size / 1024).toFixed(0)} KB</Text>
      <div className={s.actions}>
        <Button size="small" onClick={openFull}>{t.evidencePage.open}</Button>
        <Button size="small" onClick={download}>{t.evidencePage.download}</Button>
      </div>
    </div>
  );
}

export function EvidencePage() {
  const t = useT();
  const s = useStyles();
  const [selected, setSelected] = useState<EvidenceFolder | null>(null);

  const foldersQuery = useQuery({ queryKey: ['evidence-folders'], queryFn: fetchEvidenceFolders });
  const photosQuery = useQuery({
    queryKey: ['evidence-photos', selected?.prefix],
    queryFn: () => fetchEvidencePhotos(selected!.prefix),
    enabled: !!selected,
  });

  return (
    <div className={s.root}>
      <div className={s.folders}>
        <Text weight="semibold">{t.evidencePage.folders}</Text>
        {foldersQuery.isLoading ? <Spinner size="tiny" /> : null}
        {foldersQuery.data?.length === 0 ? <Text size={200}>{t.evidencePage.empty}</Text> : null}
        {foldersQuery.data?.map((f) => (
          <Button key={f.prefix} className={s.folder} appearance={selected?.prefix === f.prefix ? 'primary' : 'subtle'} onClick={() => setSelected(f)}>
            {f.kind === 'pending' ? `Pendientes — ${f.label}` : f.label}
          </Button>
        ))}
      </div>

      <div className={s.photos}>
        {!selected ? <Text>Selecciona una carpeta para ver sus fotos.</Text> : null}
        {selected && photosQuery.isLoading ? <Spinner /> : null}
        {selected && photosQuery.data?.length === 0 ? <Text>Esta carpeta no tiene fotos.</Text> : null}
        {photosQuery.data?.map((p) => <PhotoCard key={p.key} photo={p} />)}
      </div>
    </div>
  );
}
